import { useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Types
import { ISidePanelSelection, TAdvisorWashoutContainerProps } from "@/Washouts/types/advisors";
import { IPartialRecalculateWashoutInput, IRecalculateWashoutsParams } from "@/Washouts/types/Api/commons";

// API's
import { recalculateWashout } from "@/Washouts/api/common/mutations";

// Helpers
import { handleWashoutSubscriptions } from "@/Washouts/helpers/washoutSubscriptions";
import useApproveRetractWashoutSubscription from "@/Washouts/hooks/WashoutReview/useApproveRetractWashoutSubscription";
import useApproveAllWashoutSubscription from "@/Washouts/hooks/common/useApproveAllWashoutSubscription";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Components
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import AdvisorWashoutContent from "./AdvisorWashoutContent";
import UpdatedOn from "@/Washouts/components/UpdatedOn";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

const POSITION_WITHOUT_SPACES_FALLBACK = "advisors";

export default function AdvisorWashoutContainer(props: Readonly<TAdvisorWashoutContainerProps>) {
	const {
		location,
		selected,
		subscriptionsInformation,
		updateSidePanelCallBack,
		position,
		positionWithoutSpaces = POSITION_WITHOUT_SPACES_FALLBACK,
		actions,
		activeCommissionCycles = [],
	} = props;

	const queryClient = useQueryClient();

	const { useWashoutQueries } = useFactoryWashout();
	const washoutQueries: Record<string, Function | null> = useWashoutQueries(props?.position);

	const selectedRef = useRef<ISidePanelSelection | null>(null);
	const prevSubsKey = useRef<string>("");

	const { getSubscription } = useSubscriptions();

	const { data: model, isLoading } = useQuery({
		queryKey: ["getWashout", props.selected?.payPeriodId, props.position, props.selected?.userId, props.selected?.commissionCycleId],
		queryFn: async () => {
			const { locationId } = props.location || {};
			const { userId, commissionCycleId, payPeriodId } = props.selected || {};
			if (washoutQueries?.getWashout) {
				return washoutQueries.getWashout(locationId, userId, commissionCycleId, payPeriodId, position);
			}
			return null;
		},
		enabled: !!props.location && !!props.selected,
		staleTime: Infinity,
	});

	const { mutate: recalculate } = useMutation({
		mutationKey: ["recalculateWashout"],
		mutationFn: (params: IRecalculateWashoutsParams) => recalculateWashout(params),
	});

	useApproveRetractWashoutSubscription({
		locationId: props.location?.locationId,
		commissionCycleId: props.selected?.commissionCycleId,
		payPeriodId: props.selected?.payPeriodId,
		updateWashoutModel: true,
	});

	useApproveAllWashoutSubscription({
		locationId: props.location?.locationId,
		commissionCycleId: props?.selected?.commissionCycleId ?? "",
		payPeriodId: props?.selected?.payPeriodId ?? "",
	});

	const handleSummaryChange = (input: IPartialRecalculateWashoutInput) => {
		if (!input || !location || !model) {
			return;
		}
		const params: IRecalculateWashoutsParams = {
			input,
			position,
			isDepartmental: false,
			locationId: location.locationId,
			washoutId: model._id,
		};
		recalculate(params);
	};

	const { useAuth } = useAuthorization();
	const overrideCompensation = useAuth(actions?.OVERRIDE_COMPENSATION);

	const subsPayload = useMemo(
		() => ({
			locationId: location?.locationId,
			userId: selected?.userId,
			payPeriodId: selected?.payPeriodId,
			commissionCycleId: selected?.commissionCycleId,
			position,
			positionWithoutSpaces,
			activeCommissionCycles,
			subscriptionsInformation,
		}),
		[
			location?.locationId,
			selected?.userId,
			selected?.payPeriodId,
			selected?.commissionCycleId,
			position,
			positionWithoutSpaces,
			activeCommissionCycles,
			subscriptionsInformation,
		]
	);

	useEffect(() => {
		if (!subsPayload.locationId || !subsPayload.userId) {
			return;
		}

		const key = JSON.stringify(subsPayload);
		if (prevSubsKey.current === key) {
			return;
		}
		prevSubsKey.current = key;

		selectedRef.current = selected;

		handleWashoutSubscriptions({
			subscriptionsInformation: subsPayload.subscriptionsInformation,
			props: {
				location,
				selected,
				positionWithoutSpaces: subsPayload.positionWithoutSpaces,
				activeCommissionCycles: subsPayload.activeCommissionCycles,
			},
			actions: {
				// @ts-ignore
				queryClient,
				getSubscription,
			},
			options: {
				useRanking: true,
			},
		});

		if (selected) {
			updateSidePanelCallBack?.(selected);
		}
	}, [subsPayload, selected, location, queryClient, getSubscription, updateSidePanelCallBack]);

	return (
		<>
			<AdvisorWashoutContent
				model={model}
				locationId={props.location.locationId}
				isLoading={isLoading}
				overrideCompensation={overrideCompensation}
				selected={props.selected}
				handleSummaryChange={handleSummaryChange}
				positionWithoutSpaces={props?.positionWithoutSpaces}
			/>

			{!model && !isLoading && (
				<RoundedBox>
					<EmptyBody
						image={props.selected ? emptyTable : empty}
						title={props.selected ? "No washout found for this manager" : `Select a ${props?.position} to view their washout`}
						bodyHeight={"510px"}
						sx={{
							boxShadow: "none !important",
						}}
					/>
				</RoundedBox>
			)}

			<UpdatedOn isLoading={isLoading} updatedOn={model?.created} />
		</>
	);
}
